import { useCallback, useEffect, useState } from 'react';
import { HistoryFilterBar, type HistoryFilters } from '../../components/HistoryFilterBar';
import { TERMINAL_STATUS_OPTIONS } from '../../components/historyQuery';
import { ExportButton } from '../../components/ExportButton';
import { createLogger } from '../../utils/logger';
import { deleteJob, listHistoryJobs, rerunJob, fetchThumbnailBlobUrl } from '../api';
import { useBlobUrl } from '../hooks/useBlobUrl';
import { calculateVideoJobCost } from '../hooks/useVideoStats';
import type { Job } from '../types';
import { jobEndpointCosts } from '../utils/falPricing';
import { PlayerModal, type PlayerMode } from './PlayerModal';
import { AdminAction } from '../../components/PermissionGate';
import { SourceBadge } from '../../components/SourceBadge';

const log = createLogger('VideoHistoryTable');

/**
 * Format a per-scan FAL cost. Tiered precision matches lightning's
 * `formatCost` helper so the History table reads consistently across
 * apps for sub-cent / sub-dollar / multi-dollar values.
 */
function formatCost(amount: number): string {
  if (amount === 0) return '—';
  if (amount < 0.01) return `$${amount.toFixed(4)}`;
  if (amount < 1) return `$${amount.toFixed(3)}`;
  return `$${amount.toFixed(2)}`;
}

// Compact two-line date-time formatter so the "Completed At" column
// stays narrow without truncating the day. Renders as "Apr 28, 2026"
// over "10:42 AM" in the table body; falls back to the full locale
// string in the cell title for hover detail.
const COMPACT_DATE_FMT = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});
const COMPACT_TIME_FMT = new Intl.DateTimeFormat(undefined, {
  hour: 'numeric',
  minute: '2-digit',
});

function formatCompactDateTime(value: string | null): {
  date: string;
  time: string;
  full: string;
} {
  if (!value) return { date: '—', time: '', full: '' };
  const d = new Date(value);
  return {
    date: COMPACT_DATE_FMT.format(d),
    time: COMPACT_TIME_FMT.format(d),
    full: d.toLocaleString(),
  };
}

function formatDuration(seconds: number | null): string {
  if (seconds == null) return '—';
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`;
}

const BADGE = 'px-3 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1.5';

/** Merch's status badge, verbatim — solid tint plus a glyph, not a dot pill. */
function StatusBadge({ status }: { status: Job['status'] }) {
  if (status === 'completed') {
    return (
      <span className={`${BADGE} status-completed`}>
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
        <span className="status-text">Completed</span>
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span className={`${BADGE} status-failed`}>
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
        <span className="status-text">Failed</span>
      </span>
    );
  }
  // History only serves terminal jobs, so this is a safety net rather than a
  // case the table shows — the live statuses have their own tints already.
  return (
    <span className={`${BADGE} status-${status}`}>
      <span className="status-text capitalize">{status}</span>
    </span>
  );
}

interface ThumbnailProps {
  job: Job;
  onClick?: () => void;
}

function Thumbnail({ job, onClick }: ThumbnailProps) {
  const [errored, setErrored] = useState(false);

  const fetcher = useCallback(
    (signal: AbortSignal) => fetchThumbnailBlobUrl(job.job_id, signal),
    [job.job_id],
  );
  const thumbSrc = useBlobUrl(job.status === 'completed' ? fetcher : null);

  const showImage = job.status === 'completed' && !errored && thumbSrc;
  const baseClass =
    'h-8 w-14 rounded-md border border-primary-100 bg-primary-50 flex items-center justify-center overflow-hidden shrink-0';
  if (!showImage) {
    return (
      <div className={baseClass} aria-hidden>
        <svg className="w-4 h-4 text-primary-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      </div>
    );
  }
  const img = (
    <img
      src={thumbSrc}
      alt=""
      decoding="async"
      onError={() => setErrored(true)}
      className="w-full h-full object-cover"
    />
  );
  if (!onClick) {
    return <div className={baseClass}>{img}</div>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${baseClass} hover:ring-2 hover:ring-primary-300 transition-shadow cursor-pointer`}
      title="View video"
      aria-label="View video"
    >
      {img}
    </button>
  );
}

function UVCampLink({ link }: { link: string | null }) {
  if (!link) return <span className="text-primary-300">—</span>;
  return (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary-600 hover:text-primary-700 hover:underline font-medium text-xs"
      title={link}
    >
      Open
      <svg className="w-3 h-3 inline-block ml-0.5 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
        />
      </svg>
    </a>
  );
}

interface ActionsCellProps {
  job: Job;
  busyId: string | null;
  copiedId: string | null;
  onView: (job: Job) => void;
  onCopy: (job: Job) => void;
  onRerun: (job: Job) => void;
  onDelete: (job: Job) => void;
}

/**
 * Row actions, matching lightning's set and icons: View (opens the player,
 * which carries the 360 tab, the debug panel and the download), Copy (the
 * service response JSON), Re-run, Delete. The player subsumes the old
 * standalone Download button, and the debug panel's Request tab subsumes the
 * old Config button.
 */
function ActionsCell({ job, busyId, copiedId, onView, onCopy, onRerun, onDelete }: ActionsCellProps) {
  const busy = busyId === job.job_id;
  const copied = copiedId === job.job_id;
  const iconBtn =
    'p-1.5 text-xs font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center';

  return (
    <div className="inline-flex items-center gap-2 flex-nowrap justify-end">
      {job.status === 'completed' && (
        <button
          type="button"
          onClick={() => onView(job)}
          disabled={busy}
          className={`${iconBtn} text-[#70B77E] bg-[#70B77E]/10 hover:bg-[#70B77E]/20`}
          title="View the rendered video (360 view, debug panel and download are inside)"
          aria-label="View the rendered video"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </button>
      )}
      <button
        type="button"
        onClick={() => onCopy(job)}
        disabled={!job.result}
        className={`${iconBtn} text-slate-700 bg-slate-50 hover:bg-slate-100`}
        title={
          !job.result
            ? 'No result available'
            : copied
            ? 'Copied!'
            : 'Copy the service response JSON to clipboard'
        }
        aria-label={
          !job.result
            ? 'No result available'
            : copied
            ? 'Copied'
            : 'Copy the service response JSON to clipboard'
        }
      >
        {copied ? (
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        )}
      </button>
      <button
        type="button"
        onClick={() => onRerun(job)}
        disabled={busy}
        className={`${iconBtn} text-indigo-600 bg-indigo-50 hover:bg-indigo-100`}
        title="Submit a new job with the same config"
        aria-label="Re-run this scan"
      >
        {busy ? (
          <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        ) : (
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        )}
      </button>
      <AdminAction>
        <button
          type="button"
          onClick={() => onDelete(job)}
          disabled={busy}
          className={`${iconBtn} text-[#F0544F] bg-[#F0544F]/10 hover:bg-[#F0544F]/20`}
          title="Delete this job and its rendered files"
          aria-label="Delete this scan"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </AdminAction>
    </div>
  );
}

// ---------- export ----------

const CSV_COLUMNS: Array<{ key: string; header: string; getValue: (j: Job) => unknown }> = [
  { key: 'job_id', header: 'job_id', getValue: (j) => j.job_id },
  { key: 'scan_id', header: 'scan_id', getValue: (j) => j.scan_id },
  { key: 'status', header: 'status', getValue: (j) => j.status },
  { key: 'model', header: 'model', getValue: (j) => j.config_used?.model ?? '' },
  { key: 'created_at', header: 'created_at', getValue: (j) => j.created_at },
  { key: 'started_at', header: 'started_at', getValue: (j) => j.started_at ?? '' },
  { key: 'completed_at', header: 'completed_at', getValue: (j) => j.completed_at ?? '' },
  { key: 'duration_seconds', header: 'duration_seconds', getValue: (j) => j.duration_seconds ?? '' },
  { key: 'organization_id', header: 'organization_id', getValue: (j) => j.organization_id ?? '' },
  { key: 'site_id', header: 'site_id', getValue: (j) => j.site_id ?? '' },
  { key: 'source', header: 'source', getValue: (j) => j.source ?? '' },
  { key: 'service_version', header: 'service_version', getValue: (j) => j.service_version ?? '' },
  { key: 'uvcamp_link', header: 'uvcamp_link', getValue: (j) => j.uvcamp_link ?? '' },
  { key: 'video_path', header: 'video_path', getValue: (j) => j.video_path ?? '' },
  { key: 's3_bucket', header: 's3_bucket', getValue: (j) => j.result?.S3Bucket ?? '' },
  { key: 'results_path', header: 'results_path', getValue: (j) => j.result?.ResultsPath ?? '' },
  { key: 'rendered_video', header: 'rendered_video', getValue: (j) => j.result?.RenderedFilePath?.video ?? '' },
  { key: 'rendered_hotspots', header: 'rendered_hotspots', getValue: (j) => j.result?.RenderedFilePath?.hotspots ?? '' },
  { key: 'error', header: 'error', getValue: (j) => j.error ?? '' },
  { key: 'cfg_prompt', header: 'cfg_prompt', getValue: (j) => j.config_used?.prompt ?? '' },
  { key: 'cfg_negative_prompt', header: 'cfg_negative_prompt', getValue: (j) => j.config_used?.negative_prompt ?? '' },
  { key: 'cfg_duration', header: 'cfg_duration', getValue: (j) => j.config_used?.duration ?? '' },
  { key: 'cfg_resolution', header: 'cfg_resolution', getValue: (j) => j.config_used?.resolution ?? '' },
  { key: 'cfg_aspect_ratio', header: 'cfg_aspect_ratio', getValue: (j) => j.config_used?.aspect_ratio ?? '' },
  { key: 'cfg_seed', header: 'cfg_seed', getValue: (j) => j.config_used?.seed ?? '' },
  { key: 'cfg_generate_audio', header: 'cfg_generate_audio', getValue: (j) => j.config_used?.generate_audio ?? '' },
  { key: 'cfg_auto_fix', header: 'cfg_auto_fix', getValue: (j) => j.config_used?.auto_fix ?? '' },
  { key: 'fal_total_cost', header: 'fal_total_cost', getValue: (j) => calculateVideoJobCost(j) },
  { key: 'fal_usage_json', header: 'fal_usage_json', getValue: (j) => (j.fal_usage ? JSON.stringify(j.fal_usage) : '') },
];

function csvEscape(value: unknown): string {
  if (value === null || value === undefined) return '';
  const s = String(value);
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function buildCsv(jobs: Job[]): string {
  const header = CSV_COLUMNS.map((c) => c.header).join(',');
  const lines = jobs.map((job) => CSV_COLUMNS.map((c) => csvEscape(c.getValue(job))).join(','));
  return [header, ...lines].join('\r\n');
}

function downloadFile(filename: string, content: string, mime: string): void {
  const blob = new Blob([mime.startsWith('text/csv') ? '\ufeff' : '', content], {
    type: `${mime};charset=utf-8;`,
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ---------- table ----------

const PAGE_SIZES = [20, 50, 100];

/**
 * Video history table. Columns, filters, pagination and row actions mirror
 * lightning's `HistoricalTable`; the video-specific parts are the preview
 * thumbnail, the FAL cost helper, and View opening the player (video / 360 /
 * debug panel / download) instead of an images modal. The generation model
 * lives in the player's debug panel and the CSV export rather than a column.
 */
export function HistoryTable() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState<HistoryFilters>({
    dateRange: { from: null, to: null },
    status: null,
    source: null,
    tier: null,
    q: '',
  });
  const [ignoredTerms, setIgnoredTerms] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  const [player, setPlayer] = useState<{ job: Job; mode: PlayerMode } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  // Mobile cards collapse to Scan ID / Status / Cost by default; tapping
  // a card expands it. Keyed by job_id so expansion survives re-renders.
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = useCallback((jobId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  }, []);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const resp = await listHistoryJobs(
        filters.dateRange, filters.status, page, pageSize,
        null, null, filters,
      );
      setJobs(resp.jobs);
      setTotalPages(resp.total_pages);
      setTotal(resp.total);
      setIgnoredTerms(resp.ignored_terms ?? []);
      setExpandedRows(new Set());
    } catch (ex) {
      log.error('Failed to load history:', ex);
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setIsLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // The service response, not the whole row: the internal bookkeeping
  // (fal_usage / config_used / task_params) stays out of what gets pasted
  // into a ticket. Matches merch, which copies its `result` the same way.
  const handleCopy = useCallback(async (job: Job) => {
    if (!job.result) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(job.result, null, 2));
      setCopiedId(job.job_id);
      window.setTimeout(() => {
        setCopiedId((curr) => (curr === job.job_id ? null : curr));
      }, 1500);
    } catch (ex) {
      log.error('Failed to copy the service response:', ex);
      setError('Failed to copy to clipboard');
    }
  }, []);

  const handleRerun = async (job: Job) => {
    setBusyId(job.job_id);
    setError(null);
    try {
      await rerunJob(job.job_id);
      await loadData();
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (job: Job) => {
    if (!window.confirm(`Delete job for scan ${job.scan_id}? This will also remove the saved MP4.`)) {
      return;
    }
    setBusyId(job.job_id);
    setError(null);
    try {
      await deleteJob(job.job_id);
      await loadData();
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setBusyId(null);
    }
  };

  // Export covers the whole filtered window, not just the visible page, so
  // it walks the pages the way the stats hooks do.
  const handleExport = async (format: 'csv' | 'json') => {
    setIsExporting(true);
    setError(null);
    try {
      const all: Job[] = [];
      const exportPageSize = 500;
      let p = 1;
      let pages = 1;
      do {
        const resp = await listHistoryJobs(
          filters.dateRange, filters.status, p, exportPageSize,
          null, null, filters,
        );
        all.push(...resp.jobs);
        pages = resp.total_pages;
        if (resp.jobs.length === 0) break;
        p += 1;
      } while (p <= pages);

      const ts = new Date().toISOString().replace(/[:.]/g, '-');
      if (format === 'csv') {
        downloadFile(`360_video_history_${ts}.csv`, buildCsv(all), 'text/csv');
      } else {
        downloadFile(`360_video_history_${ts}.json`, JSON.stringify(all, null, 2), 'application/json');
      }
    } catch (ex) {
      log.error('Export failed:', ex);
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setIsExporting(false);
    }
  };

  const handleFiltersChange = (next: HistoryFilters) => {
    setFilters(next);
    setPage(1);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-soft overflow-hidden transition-all duration-200">
      {/* Header with filters */}
      <div className="px-5 py-4 border-b border-slate-100">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Scan History</h2>
            <p className="text-sm text-slate-500">
              {total.toLocaleString()} total scan{total !== 1 ? 's' : ''}
            </p>
          </div>

        </div>

        <HistoryFilterBar
          app="video"
          value={filters}
          onChange={handleFiltersChange}
          onApply={loadData}
          statusOptions={TERMINAL_STATUS_OPTIONS}
          ignoredTerms={ignoredTerms}
        >
          <ExportButton onExport={handleExport} isLoading={isExporting} />
        </HistoryFilterBar>
      </div>

      {error && (
        <div className="mx-5 mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="p-12 text-center">
          <div className="inline-flex items-center gap-3 text-slate-500">
            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Loading...
          </div>
        </div>
      ) : jobs.length === 0 ? (
        <div className="p-12 text-center">
          <div className="inline-flex flex-col items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center">
              <svg className="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <p className="text-slate-900 font-medium">No historical data</p>
              <p className="text-sm text-slate-500">Try adjusting your date range or filters</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          {/* Compact layout: every body cell uses `whitespace-nowrap`
              so rows stay one line tall. The parent `overflow-x-auto`
              handles long content by scrolling horizontally instead of
              wrapping. The Error column is the only deliberately
              wrapped cell — error messages are rare but worth reading
              in full when present. */}
          <table className="data-table mobile-collapsible min-w-full text-sm">
            <thead className="bg-primary-50/60 text-primary-600 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="pl-3 pr-2 py-2 text-left font-semibold w-16"></th>
                <th className="px-3 py-2 text-left font-semibold">Scan ID</th>
                <th className="px-3 py-2 text-left font-semibold">UVCamp</th>
                <th className="px-3 py-2 text-left font-semibold">Source</th>
                <th className="px-3 py-2 text-left font-semibold">Organization</th>
                <th className="px-3 py-2 text-left font-semibold">Status</th>
                <th className="px-3 py-2 text-left font-semibold">Completed</th>
                <th className="px-3 py-2 text-left font-semibold">Duration</th>
                <th className="px-3 py-2 text-left font-semibold">Cost</th>
                <th className="px-3 py-2 text-left font-semibold">Error</th>
                <th className="px-3 py-2 text-left font-semibold">Job ID</th>
                <th className="px-3 py-2 text-left font-semibold">BE Version</th>
                <th className="px-3 py-2 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-primary-100">
              {jobs.map((job) => {
                const ts = formatCompactDateTime(job.completed_at);
                return (
                <tr
                  key={job.job_id}
                  className="hover:bg-primary-50/40 align-middle"
                  data-collapsed={!expandedRows.has(job.job_id)}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest('button, a, input, select')) return;
                    toggleRow(job.job_id);
                  }}
                >
                  <td className="pl-3 pr-2 py-2" data-label="Preview">
                    <Thumbnail
                      job={job}
                      onClick={
                        job.status === 'completed'
                          ? () => setPlayer({ job, mode: 'video' })
                          : undefined
                      }
                    />
                  </td>
                  <td
                    className="px-3 py-2 font-mono text-[11px] text-primary-900 whitespace-nowrap"
                    title={job.scan_id}
                    data-label="Scan ID"
                  >
                    {job.scan_id}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap" data-label="UVCamp"><UVCampLink link={job.uvcamp_link} /></td>
                  <td className="px-3 py-2 whitespace-nowrap" data-label="Source"><SourceBadge source={job.source} baseClassName={BADGE} /></td>
                  <td
                    className="px-3 py-2 font-mono text-xs text-slate-500 whitespace-nowrap"
                    title={job.organization_id ?? undefined}
                    data-label="Organization"
                  >
                    {job.organization_id ? (
                      job.organization_id.length > 12
                        ? `${job.organization_id.substring(0, 12)}...`
                        : job.organization_id
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap" data-label="Status"><StatusBadge status={job.status} /></td>
                  <td
                    className="px-3 py-2 text-[11px] text-primary-700 whitespace-nowrap leading-tight"
                    title={ts.full}
                    data-label="Completed"
                  >
                    <div>{ts.date}</div>
                    {ts.time && <div className="text-primary-500">{ts.time}</div>}
                  </td>
                  <td className="px-3 py-2 text-primary-700 font-mono text-xs whitespace-nowrap" data-label="Duration">
                    {formatDuration(job.duration_seconds)}
                  </td>
                  <td
                    className="px-3 py-2 text-primary-700 font-mono text-xs whitespace-nowrap"
                    data-label="Cost"
                    title={(() => {
                      if (!job.fal_usage) return 'No FAL usage data recorded for this job';
                      const breakdown = jobEndpointCosts(job);
                      const callsByEp = job.fal_usage;
                      return breakdown
                        .map((ec) => {
                          const calls = callsByEp[ec.endpoint]?.calls ?? 0;
                          const tag =
                            ec.source === 'billed'
                              ? '(billed)'
                              : ec.source === 'estimated'
                              ? '(estimated)'
                              : '(estimate unavailable)';
                          return `${ec.endpoint}: $${ec.cost.toFixed(4)} ${tag} — ${calls} calls`;
                        })
                        .join('\n');
                    })()}
                  >
                    {(() => {
                      const cost = calculateVideoJobCost(job);
                      return cost > 0 ? (
                        formatCost(cost)
                      ) : (
                        <span className="text-primary-300">—</span>
                      );
                    })()}
                  </td>
                  <td
                    className="px-3 py-2 text-[11px] text-red-700 max-w-[14rem] truncate"
                    title={job.error ?? undefined}
                    data-label="Error"
                  >
                    {job.error || <span className="text-primary-300">—</span>}
                  </td>
                  <td
                    className="px-3 py-2 font-mono text-xs text-slate-400 whitespace-nowrap"
                    title={job.job_id}
                    data-label="Job ID"
                  >
                    {job.job_id.substring(0, 8)}...
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap" data-label="BE Version">
                    {job.service_version ? (
                      <span
                        className="font-mono text-xs px-2 py-0.5 rounded-md bg-slate-100 text-slate-700"
                        title={job.service_version}
                      >
                        {job.service_version}
                      </span>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap" data-label="Actions">
                    <ActionsCell
                      job={job}
                      busyId={busyId}
                      copiedId={copiedId}
                      onView={(j) => setPlayer({ job: j, mode: 'video' })}
                      onCopy={handleCopy}
                      onRerun={handleRerun}
                      onDelete={handleDelete}
                    />
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-center md:justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4 flex-wrap justify-center md:justify-start">
          <div className="text-xs text-slate-500">
            {total > 0
              ? `Showing ${((page - 1) * pageSize) + 1} - ${Math.min(page * pageSize, total)} of ${total.toLocaleString()}`
              : 'No results'}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-slate-500">Rows:</span>
            {PAGE_SIZES.map((size) => (
              <button
                key={size}
                onClick={() => handlePageSizeChange(size)}
                className={`px-2 py-0.5 rounded-md text-xs font-medium transition-all duration-200 ${
                  pageSize === size
                    ? 'bg-indigo-50 text-indigo-600'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {size}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-nowrap shrink-0">
          <button
            onClick={() => setPage(1)}
            disabled={page === 1}
            title="First page"
            aria-label="First page"
            className="btn-secondary px-2 py-1 rounded-md text-xs disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {'<<'}
          </button>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            title="Previous page"
            aria-label="Previous page"
            className="btn-secondary px-2 py-1 rounded-md text-xs disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {'<'}
          </button>
          <span className="px-1.5 py-1 text-xs text-slate-600 whitespace-nowrap">
            {page}/{totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            title="Next page"
            aria-label="Next page"
            className="btn-secondary px-2 py-1 rounded-md text-xs disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {'>'}
          </button>
          <button
            onClick={() => setPage(totalPages)}
            disabled={page === totalPages}
            title="Last page"
            aria-label="Last page"
            className="btn-secondary px-2 py-1 rounded-md text-xs disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {'>>'}
          </button>
        </div>
      </div>

      <PlayerModal
        job={player?.job ?? null}
        initialMode={player?.mode ?? 'video'}
        onClose={() => setPlayer(null)}
      />
    </div>
  );
}
